import { ArrowUpRight, ClipboardList, Hexagon, ShieldCheck } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useSession } from '@/lib/platform/hooks';

/**
 * THESIS: An open register desk makes the product legible before a visitor signs in; it refuses
 * the usual marketing hero in favor of the work itself. OWN-WORLD: wax-paper ground, leaf ink,
 * pollen signal, ruled ledger rows. STORY: a beekeeper sees where hive records and receipts live,
 * then chooses an account or returns to their registry. FIRST VIEWPORT: a slim desk bar, a compact
 * register plane, and one honest empty row. FORM: working registry desk / ledger. FINISH: unreviewed
 * and undocumented is unfinished; this build ends with the finish review, the verdict, DESIGN.md,
 * and every shipping raster carrying its provenance
 */
export function LandingPage() {
  const session = useSession();
  const isAuthed = session.status === 'authed';

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/80 bg-background/95">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-md text-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Hexagon className="size-4" strokeWidth={2.2} />
            </span>
            <span className="text-sm font-semibold tracking-tight">Hive Register</span>
          </Link>

          <nav aria-label="Account" className="flex items-center gap-2">
            {isAuthed ? (
              <Button render={<Link to="/hives" data-jr-key="landing-registry" />} variant="outline" size="sm">
                Open registry <ArrowUpRight data-icon="inline-end" />
              </Button>
            ) : session.status === 'unavailable' ? (
              <span className="text-xs text-muted-foreground">Registry connection unavailable</span>
            ) : (
              <>
                <Button render={<Link to="/sign-in" data-jr-key="landing-sign-in" />} variant="ghost" size="sm">
                  Sign in
                </Button>
                <Button render={<Link to="/sign-up" data-jr-key="landing-sign-up" />} size="sm">
                  Create account
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      <div className="mx-auto w-full max-w-6xl px-5 py-12 sm:px-8 sm:py-20">
        <section className="grid gap-12 lg:grid-cols-[minmax(0,0.75fr)_minmax(480px,1.25fr)] lg:items-start lg:gap-20">
          <div className="max-w-xl pt-2">
            <div className="mb-7 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <span className="size-2 rounded-full bg-accent" aria-hidden="true" />
              National hive register
            </div>
            <h1 className="max-w-[11ch] text-5xl font-semibold leading-[0.98] tracking-[-0.045em] text-foreground sm:text-6xl">
              Keep every hive and receipt in one place.
            </h1>
            <p className="mt-7 max-w-[46ch] text-base leading-7 text-muted-foreground sm:text-lg">
              Hive Register gives beekeepers a clear working record for the hives they register nationally—and a
              dependable home for the receipts that follow.
            </p>
            {!isAuthed && session.status !== 'unavailable' && (
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button render={<Link to="/sign-up" data-jr-key="landing-sign-up-primary" />} size="lg">
                  Start your register <ArrowUpRight data-icon="inline-end" />
                </Button>
                <span className="text-sm text-muted-foreground">Already have an account?</span>
                <Link
                  to="/sign-in"
                  data-jr-key="landing-sign-in-secondary"
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                >
                  Sign in
                </Link>
              </div>
            )}
            {session.status === 'unavailable' && (
              <p className="mt-8 max-w-[42ch] rounded-md border border-border bg-muted/40 px-4 py-3 text-sm leading-6 text-muted-foreground">
                We could not reach the registry right now. Your records are not being changed; please try again shortly.
              </p>
            )}
          </div>

          <section
            aria-labelledby="register-heading"
            className="overflow-hidden rounded-xl border border-border bg-card shadow-[0_16px_40px_-28px_color-mix(in_oklch,var(--foreground)_35%,transparent)]"
          >
            <div className="flex items-start justify-between gap-4 px-5 py-5 sm:px-7">
              <div>
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <ClipboardList className="size-4 text-primary" />
                  Working register
                </div>
                <h2 id="register-heading" className="mt-2 text-xl font-semibold tracking-[-0.025em]">
                  Your hives, clearly accounted for.
                </h2>
              </div>
              <Badge variant="outline" className="mt-0.5 shrink-0">
                No entries yet
              </Badge>
            </div>
            <Separator />
            <div className="grid grid-cols-[1.3fr_0.8fr_0.9fr] gap-3 border-b border-border bg-muted/35 px-5 py-3 text-[0.68rem] font-medium uppercase tracking-[0.12em] text-muted-foreground sm:px-7">
              <span>Hive</span>
              <span>Registration</span>
              <span>Receipt</span>
            </div>
            <div className="px-5 py-12 sm:px-7 sm:py-16">
              <div className="mx-auto flex max-w-sm flex-col items-center text-center">
                <div className="mb-4 flex size-11 items-center justify-center rounded-full border border-border bg-muted text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <p className="text-sm font-medium text-foreground">The register is ready for your first hive.</p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Once you add one, its national registration and receipt will stay together here.
                </p>
                {isAuthed && (
                  <Button
                    render={<Link to="/hives" data-jr-key="landing-registry-empty" />}
                    variant="outline"
                    size="sm"
                    className="mt-6"
                  >
                    Go to the registry <ArrowUpRight data-icon="inline-end" />
                  </Button>
                )}
              </div>
            </div>
            <div className="border-t border-border bg-muted/20 px-5 py-3 text-xs text-muted-foreground sm:px-7">
              Receipts remain attached to the record they belong to.
            </div>
          </section>
        </section>
      </div>
    </main>
  );
}
